import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import EmptyScreensReportAccountTemplatesComponent from 'webapp/components/empty-screens/report-account-templates';
import TemplatesAccountGridComponent from 'webapp/components/templates/account/grid';

import type { Board, BoardLoop } from 'webapp/models-custom';
import type Report from 'webapp/models-custom/report';
import type Template from 'webapp/models-custom/template';
import type ReportRecord from 'webapp/models/report';

export default class ReportsAccountTemplatesRouteTemplate extends Component {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('router') declare routerService: Service.Router;
	@service('account') declare accountService: Service.Account;

	get model(): Report[] {
		return this.mmReportsService.accountTemplateReportModels;
	}

	get hasTemplates(): boolean {
		return !!this.model.length;
	}

	@action
	createTemplate(): void {
		if (!this.mmReportsService.normalReportModels.length) {
			this.infolayerService.error(
				'To create an account template please create a report first.',
			);
			return;
		}

		this.mmReportsService.openAccountTemplateEditPopup(null, 'account_templates');
	}

	@action
	useTemplate(model: Template): void {
		const afterUse = (report: ReportRecord) => {
			const isAccountTemplatesPath = this.routerService.currentRouteName?.startsWith(
				'reports.account-templates',
			);

			let afterTransition = () => {
				/* do nothing */
			};

			if (isAccountTemplatesPath) {
				afterTransition = () => {
					const reportModel = this.mmReportsService.createModel(report);
					this.mmReportsService.openPreviewPopup(reportModel);
				};
			}

			this.navigationService.transitionToRoute('reports.index').then(afterTransition);
		};

		this.mmReportsService.createReportFromTemplate(model, 'report_account_template', afterUse);
	}

	@action
	previewTemplate(model: Template): void {
		this.mmTemplatesService.openPreviewPopup(model, () => {
			this.useTemplate(model);
		});
	}

	@action
	editTemplate(model: Board | Report | BoardLoop): void {
		this.mmReportsService.editReport(model as Report);
	}

	@action
	deleteTemplate(model: Board | Report | BoardLoop): void {
		const template = (model as Report).templateModel;
		if (template) {
			void this.mmTemplatesService.deleteTemplate(template);
		}
	}

	<template>
		{{#if this.hasTemplates}}
			<div id="account-templates" class="view-grid">
				<TemplatesAccountGridComponent
					@model={{this.model}}
					@createTemplate={{this.createTemplate}}
					@useTemplate={{this.useTemplate}}
					@previewTemplate={{this.previewTemplate}}
					@editTemplate={{this.editTemplate}}
					@deleteTemplate={{this.deleteTemplate}}
				/>
			</div>
		{{else}}
			<EmptyScreensReportAccountTemplatesComponent @onCreate={{this.createTemplate}} />
		{{/if}}
	</template>
}
